<template>
    <div class="flex flex-col items-center gap-1 rounded-lg bg-panel px-2 pt-2 pb-1.5" :aria-label="ariaLabel" role="group">
        <div class="text-[0.8125rem] leading-tight font-semibold text-body">{{ label }} ({{ total }})</div>
        <DoughnutChart
            :values="chartValues"
            :colors="chartSegmentColors"
            :size="size"
            :spacing="segmentSpacing"
            :border-radius="segmentSpacing"
            :center-label="`${compliantPercentage}%`"
            :center-label-style="centerLabelStyle"
        />
        <div class="mt-0.5 flex min-h-[22px] flex-wrap items-center justify-center gap-2">
            <span v-if="!hasAssets" class="text-xs font-medium text-gray-400">
                {{ trans('dashboard.v2.testing_compliance.by_asset_type.no_assets') }}
            </span>
            <span v-if="inoperable > 0" class="inline-flex items-center gap-0.5 text-xs font-semibold text-body">
                <TestingComplianceStatusIcon status="inoperable" :size="18" />
                <span class="pr-1">{{ inoperable }}</span>
            </span>
            <span v-if="approved > 0" class="inline-flex items-center gap-0.5 text-xs font-semibold text-body">
                <TestingComplianceStatusIcon status="approved" :size="18" />
                <span class="pr-1">{{ approved }}</span>
            </span>
            <span v-if="pending > 0" class="inline-flex items-center gap-0.5 text-xs font-semibold text-body">
                <TestingComplianceStatusIcon status="pending" :size="18" />
                <span class="pr-1">{{ pending }}</span>
            </span>
            <span v-if="untested > 0" class="inline-flex items-center gap-0.5 text-xs font-semibold text-body">
                <TestingComplianceStatusIcon status="untested" :size="18" />
                <span class="pr-1">{{ untested }}</span>
            </span>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import DoughnutChart from '@/Components/Charts/DoughnutChart.vue';
import TestingComplianceStatusIcon from './TestingComplianceStatusIcon.vue';
import { trans } from '@/mixins/i18n';
import { chartColors } from '@/Utils/chartDefaults';

const props = defineProps({
    label: { type: String, required: true },
    untested: { type: Number, required: true },
    pending: { type: Number, required: true },
    approved: { type: Number, required: true },
    // Defaults to 0 so the legacy donut layout (untested/pending/approved
    // only) keeps working unchanged. The B-variant widget opts in by
    // passing a real count, which adds a red inoperable segment and chip.
    inoperable: { type: Number, default: 0 },
    compliantPercentage: { type: Number, required: true },
    size: { type: Number, default: 120 },
});

const total = computed(() => props.untested + props.pending + props.approved + props.inoperable);
const hasAssets = computed(() => total.value > 0);

// Chart.js doughnut draws segments clockwise from 12 o'clock. Both variants
// share the same ordering — inoperable (red) at 12 going right, then
// approved (green), pending (blue), untested (gray) closing the ring back
// at 12. The inoperable slot is omitted entirely when zero so the A
// variant renders the remaining three segments in the same clockwise order.
const segments = computed(() =>
    [
        { value: props.inoperable, color: chartColors.complianceInoperable },
        { value: props.approved, color: chartColors.complianceApproved },
        { value: props.pending, color: chartColors.compliancePending },
        { value: props.untested, color: chartColors.complianceUntested },
    ].filter((s, idx) => idx !== 0 || s.value > 0),
);

const chartValues = computed(() => (hasAssets.value ? segments.value.map((s) => s.value) : [1]));

const chartSegmentColors = computed(() => (hasAssets.value ? segments.value.map((s) => s.color) : [chartColors.complianceUntested]));

// Drop chart.js inter-segment spacing when only one segment is non-zero so a
// 100% donut renders as a closed ring instead of a gapped one.
const segmentSpacing = computed(() => {
    const nonZero = segments.value.filter((s) => s.value > 0).length;
    return nonZero <= 1 ? 0 : 2;
});

const ariaLabel = computed(() => {
    const parts = [
        `${props.approved} ${trans('dashboard.v2.testing_compliance.status.approved')}`,
        `${props.pending} ${trans('dashboard.v2.testing_compliance.status.pending')}`,
        `${props.untested} ${trans('dashboard.v2.testing_compliance.status.untested')}`,
    ];
    if (props.inoperable > 0) {
        parts.push(`${props.inoperable} ${trans('dashboard.v2.testing_compliance.status.inoperable')}`);
    }
    return `${props.label}: ${parts.join(', ')}`;
});

const centerLabelStyle = computed(() => ({
    color: hasAssets.value ? chartColors.body : chartColors.grey,
    fontSize: '20px',
    fontWeight: '700',
    lineHeight: '20px',
}));
</script>
